import { ChangeEvent, MutableRefObject, useContext, useEffect, useState } from 'react';
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridEditSingleSelectCellProps,
  GridRenderCellParams,
  GridRowId,
  GridRowModes,
  GridRowModesModel,
  useGridApiContext,
  GridValueGetterParams
} from '@mui/x-data-grid';
import { MetadataError } from '@ep/error-handling';
import { Box, IconButton, InputBase, Theme, Tooltip, Typography, useTheme } from '@mui/material';
import { File, MetricRecord, ReportingPeriod } from '@esg/esg-global-types';
import {
  CreateMetricRecordClientData,
  MetricRecordFilters,
  UpdateMetricRecordClientData
} from '@esg/syntiro-api-core';
import { GridApiCommunity } from '@mui/x-data-grid/internals';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Close';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { ReactComponent as EditIcon } from '../../../assets/icons/Edit.svg';
import { ReactComponent as FileIcon } from '../../../assets/icons/Attach.svg';
import { ReactComponent as FolderIcon } from '../../../assets/icons/Folder.svg';
import { ReactComponent as CopyIcon } from '../../../assets/icons/Paste.svg';
import { ReactComponent as DeleteIcon } from '../../../assets/icons/Delete.svg';
import { ReactComponent as ResponseYesIcon } from '../../../assets/icons/Correct.svg';
import { ReactComponent as ResponseNoIcon } from '../../../assets/icons/Incorrect.svg';
import { MetricRecordCaptureParameters } from '../../../views/metric_collection/MetricCollectionDataManagement';
import { MetricExtended } from '../../../lib/metric_capture/metric';
import ExpandValueEditModal from './ExpandValueEditModal';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import { UserContext } from '../../../context/UserContext';
import { GroupContext } from '../../../context/GroupContext';
import { CompanyContext } from '../../../context/CompanyContext';
import { reportingPeriodFilterByGroup } from '../../../lib/metric_capture/reporting_period';
import { ViewAccess, getViewAccess } from '../../../util/user_access';
import {
  MetricRecordEntitiesMap,
  createMetricRecord,
  metric_record_label,
  updateMetricRecord,
  getMetricRecords,
  softDeleteMetricRecord
} from '../../../graphql/services/metric_record.service';
import { getFilesForMetricRecords } from '../../../lib/metric_capture/file';
import { generateUuidV4 } from '../../../util/uuid';
import { log } from '../../../util/log';
import UploadSupportingDocsModal from '../supporting_docs/UploadSupportingDocsModal';
import ManageSupportingDocsModal from '../supporting_docs/ManageSupportingDocsModal';
import DataGridNoRows from '../../shared/datagrid/DataGridNoRows';
import CopyQualitativeMetricRecordModal from './CopyQualitativeMetricRecordModal';
import { QualitativeMetricRecordGridToolbar } from './QualitativeMetricRecordGridToolbar';
import { values } from '../../../util/general_utils';
import { mapQualitativeMetricRecordInputForRequest } from '../../../graphql/utilities/metric_record.util';
import DeleteConfirmModal from '../../shared/modal/DeleteConfirmModal';

interface MetricRecordGridQualitativeProps {
  capture_parameters: MetricRecordCaptureParameters;
  entities_map: MetricRecordEntitiesMap;
}

export interface QualitativeMetricRecordGridRow {
  id: string;
  metric: MetricExtended;
  status: boolean;
  value: string;
  docs: boolean;
  saved: boolean;
}

/**
 * Custom text input for editing the value of a qualitative Metric Record.
 * @param {GridEditSingleSelectCellProps} props Predefined attributes of an MUI Datagrid Edit Cell element.
 * @returns {JSX.Element}
 */
const CustomValueEditComponent = (props: GridEditSingleSelectCellProps) => {
  const { id, field, value }: { id: GridRowId; field: string; value?: string | number } = props;
  const [openExpand, setOpenExpand] = useState<boolean>(false);
  const [valueState, setValueState] = useState<string | number | undefined>(value);
  const api_ref: MutableRefObject<GridApiCommunity> = useGridApiContext();
  const metric: string = api_ref.current.getCellValue(id, 'metric');

  const handleOpenExpandEditModal = (): void => {
    setOpenExpand(true);
  };

  const handleCloseExpandEditModal = (): void => {
    setOpenExpand(false);
  };

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const new_value: string = event.target.value;
    setValueState(new_value);
    api_ref.current.setEditCellValue({ id, field, value: new_value, debounceMs: 200 }, event);
  };

  return (
    <>
      <ExpandValueEditModal
        open={openExpand}
        value={valueState}
        title={metric}
        handleCloseModal={handleCloseExpandEditModal}
        handleChange={handleChange}
      />
      <Box sx={{ display: 'flex', width: '100%', alignItems: 'start' }}>
        <InputBase
          multiline
          rows={4}
          value={valueState}
          sx={{ width: '100%', fontSize: '0.9rem', px: 1.5 }}
          onChange={(event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) =>
            handleChange(event)
          }
        />
        <IconButton onClick={handleOpenExpandEditModal}>
          <OpenInFullIcon sx={{ fontSize: '1rem' }} />
        </IconButton>
      </Box>
    </>
  );
};

/**
 * Datagrid handling full CRUD functionality for qualitative Metric Records
 * @param {MetricRecordCaptureParameters} capture_parameters Object of parameter fields to use for Metric Record creation and editing
 * @param {MetricRecordEntitiesMap} entities_map List of options to check for Metric Record data
 * @returns {JSX.Element}
 */
const QualitativeMetricRecordGrid = ({
  capture_parameters,
  entities_map
}: MetricRecordGridQualitativeProps) => {
  const { setFeedbackData } = useContext(FeedbackSnackbarContext);
  const { user } = useContext(UserContext);
  const { group } = useContext(GroupContext);
  const { company } = useContext(CompanyContext);

  if (!group || !company) return null;

  const [metricRecordRows, setMetricRecordRows] = useState<Array<QualitativeMetricRecordGridRow>>(
    []
  );
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [supportingDocs, setSupportingDocs] = useState<Array<File>>([]);
  const [showUploadDocsModal, setShowUploadDocsModal] = useState<boolean>(false);
  const [showManageDocsModal, setShowManageDocsModal] = useState<boolean>(false);
  const [showCopyMetricRecordModal, setShowCopyMetricRecordModal] = useState<boolean>(false);
  const [showClearConfirmModal, setShowClearConfirmModal] = useState<boolean>(false);
  const [actionRow, setActionRow] = useState<QualitativeMetricRecordGridRow | null>(null);
  const [gridLoading, setGridLoading] = useState<boolean>(true);
  const theme: Theme = useTheme();

  // Component variables
  const reporting_period_group_locked: boolean =
    (capture_parameters.reporting_period_group &&
      reportingPeriodFilterByGroup(
        capture_parameters.reporting_period_group?.id,
        values<ReportingPeriod>(entities_map.reporting_periods)
      ).every((reporting_period: ReportingPeriod) => {
        return reporting_period.locked === true;
      })) ??
    false;

  const view_access: ViewAccess =
    user && group && company
      ? getViewAccess(user, group.id, company.id, 'sustainability.metrics')
      : null;

  const view_only: boolean = reporting_period_group_locked || view_access === 'read' ? true : false;

  const metric_group_metrics = values<MetricExtended>(entities_map.metrics).filter(
    (metric: MetricExtended) =>
      metric.metric_group.id === capture_parameters.metric_group?.id &&
      'is_quantitative' in metric.standard &&
      metric.standard.is_quantitative === false
  );

  // Handler functions
  const handleEditClick = (row_id: string): void => {
    setRowModesModel({ ...rowModesModel, [row_id]: { mode: GridRowModes.Edit } });
  };

  const handleConfirmClick = (row_id: string): void => {
    setRowModesModel({ ...rowModesModel, [row_id]: { mode: GridRowModes.View } });
  };

  const handleCancelClick = (row_id: string): void => {
    setRowModesModel({
      ...rowModesModel,
      [row_id]: { mode: GridRowModes.View, ignoreModifications: true }
    });
  };

  // Handlers for the clear response modal.
  const handleClearClick = (row: QualitativeMetricRecordGridRow): void => {
    setActionRow(row);
    setShowClearConfirmModal(true);
  };

  const handleCloseClearModal = (): void => {
    setShowClearConfirmModal(false);
  };

  const handleClear = async () => {
    // Clear the contents of the response.
    if (actionRow) {
      await softDeleteMetricRecord(
        {
          group_id: group.id,
          company_id: company.id
        },
        { id: actionRow.id }
      );
      setMetricRecordRows([
        ...metricRecordRows.map((metric_record: QualitativeMetricRecordGridRow) => {
          if (actionRow.id !== metric_record.id) {
            return metric_record;
          } else {
            return {
              id: generateUuidV4(),
              metric: metric_record.metric,
              status: false,
              value: '',
              docs: false,
              saved: false
            };
          }
        })
      ]);
    }
  };

  const handleAddSupportingDoc = (row_id: string, supporting_doc: File): void => {
    setSupportingDocs((prev) => [...prev, supporting_doc]);
    setMetricRecordRows((prev) =>
      prev.map((row) => (row_id === row.id ? { ...row, docs: true } : row))
    );
  };

  const handleRemoveSupportingDoc = (row_id: string, delete_doc_id: string): void => {
    const metric_record_docs: Array<File> = supportingDocs.filter(
      (doc: File) =>
        doc.metric_record && doc.metric_record.id === row_id && doc.id !== delete_doc_id
    );
    if (metric_record_docs.length === 0) {
      const new_rows: Array<QualitativeMetricRecordGridRow> = metricRecordRows.map(
        (row: QualitativeMetricRecordGridRow) => (row_id === row.id ? { ...row, docs: false } : row)
      );
      setMetricRecordRows((prev) =>
        prev.map((row: QualitativeMetricRecordGridRow) =>
          row_id === row.id ? { ...row, docs: false } : row
        )
      );
    }
    setSupportingDocs((prev) => prev.filter((doc) => doc.id !== delete_doc_id));
  };

  const handleOpenUploadDocsModal = (action_row: QualitativeMetricRecordGridRow): void => {
    setActionRow(action_row);
    setShowUploadDocsModal(true);
  };

  const handleOpenManageDocsModal = (action_row: QualitativeMetricRecordGridRow): void => {
    setActionRow(action_row);
    setShowManageDocsModal(true);
  };

  const handleCloseUploadDocsModal = (): void => {
    setShowUploadDocsModal(false);
  };

  const handleCloseManageDocsModal = (): void => {
    setShowManageDocsModal(false);
  };

  const handleOpenCopyMetricRecordModal = (action_row: QualitativeMetricRecordGridRow): void => {
    setActionRow(action_row);
    setShowCopyMetricRecordModal(true);
  };

  const handleCloseCopyMetricRecordModal = (): void => {
    setShowCopyMetricRecordModal(false);
  };

  // Row functions
  const fetchRows = async (): Promise<void> => {
    try {
      setGridLoading(true);
      if (!group || !company || !capture_parameters.reporting_period_group) {
        throw new Error('Cannot fetch Metric Records without required parameters');
      }
      const metric_record_filters: MetricRecordFilters = {
        reporting_period_group_id: capture_parameters.reporting_period_group.id
      };
      const metric_records: Array<MetricRecord> = await getMetricRecords(
        { group_id: group.id, company_id: company.id },
        metric_record_filters
      );
      const supporting_docs: Array<File> = await getFilesForMetricRecords(group.id, company.id);
      const metric_record_rows: Array<QualitativeMetricRecordGridRow> = [];
      metric_records.forEach((metric_record: MetricRecord) => {
        const metric: MetricExtended | undefined = entities_map.metrics[metric_record.metric.id];
        if (metric && metric.metric_group.id === capture_parameters.metric_group?.id) {
          metric_record_rows.push({
            id: metric_record.id,
            metric: metric,
            status: metric_record.status ?? false,
            value: metric_record.value.toString(),
            docs:
              supporting_docs.filter((file: File) => {
                return file.metric_record && file.metric_record.id === metric_record.id;
              }).length > 0,
            saved: true
          });
        }
      });
      setSupportingDocs(supporting_docs);
      // Include metrics for group which have not been created as metric records yet.
      metric_group_metrics.forEach((metric: MetricExtended) => {
        const existing_row: QualitativeMetricRecordGridRow | undefined = metric_record_rows.find(
          (row: QualitativeMetricRecordGridRow) => row.metric.id === metric.id
        );
        if (!existing_row) {
          metric_record_rows.unshift({
            id: generateUuidV4(),
            metric: metric,
            status: false,
            value: '',
            docs: false,
            saved: false
          });
        }
      });
      setMetricRecordRows(metric_record_rows);
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: QualitativeMetricRecordGrid failed on an unknown error while calling fetchRows.',
          {
            group: group,
            company: company,
            capture_parameters: capture_parameters
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to fetch metric records. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    } finally {
      setGridLoading(false);
    }
  };

  const validateMetricRecord = (metric_record: QualitativeMetricRecordGridRow): boolean => {
    return (
      !!metric_record.metric &&
      (metric_record.status === false ||
        (metric_record.status === true && metric_record.value !== ''))
    );
  };

  const updateRow = async (
    old_metric_record: QualitativeMetricRecordGridRow,
    new_metric_record: QualitativeMetricRecordGridRow
  ): Promise<QualitativeMetricRecordGridRow | undefined> => {
    try {
      setGridLoading(true);
      // If no inputs were changed, return the old row as is.
      if (
        new_metric_record.status === old_metric_record.status &&
        new_metric_record.value === old_metric_record.value
      ) {
        return old_metric_record;
      }
      const valid_input: boolean = validateMetricRecord(new_metric_record);
      if (!valid_input) {
        setFeedbackData({
          message: `Details must be included for TRUE Status`,
          state: true,
          type: 'warning'
        });
        return old_metric_record;
      }
      if (group && company && capture_parameters.reporting_period_group) {
        if (new_metric_record.saved) {
          const update_metric_record_client_data: Partial<UpdateMetricRecordClientData> =
            mapQualitativeMetricRecordInputForRequest({
              metric: new_metric_record.metric,
              reporting_period_group: capture_parameters.reporting_period_group,
              status: new_metric_record.status,
              value: new_metric_record.value
            });
          await updateMetricRecord(
            { group_id: group.id, company_id: company.id },
            {
              ...update_metric_record_client_data,
              id: new_metric_record.id
            }
          );
          setMetricRecordRows((prev) => [
            ...prev.map((metric_record: QualitativeMetricRecordGridRow) => {
              return metric_record.id === new_metric_record.id ? new_metric_record : metric_record;
            })
          ]);
        } else {
          // Transform metric record row data into API required request data and create new metric record.
          const create_metric_record_client_data: CreateMetricRecordClientData =
            mapQualitativeMetricRecordInputForRequest({
              metric: new_metric_record.metric,
              reporting_period_group: capture_parameters.reporting_period_group,
              status: new_metric_record.status,
              value: new_metric_record.value
            });
          const created_metric_record: MetricRecord | null | undefined = await createMetricRecord(
            { group_id: group.id, company_id: company.id },
            create_metric_record_client_data,
            {
              return_data: true,
              create_audit_log: true
            }
          );
          if (created_metric_record) {
            const saved_row: QualitativeMetricRecordGridRow = {
              ...new_metric_record,
              id: created_metric_record.id,
              saved: true
            };
            setMetricRecordRows((prev) => [
              ...prev.map((metric_record) => {
                return metric_record.id === new_metric_record.id ? saved_row : metric_record;
              })
            ]);
            return saved_row;
          }
        }
        return new_metric_record;
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: QualitativeMetricRecordGrid failed on an unknown error while calling updateRow.',
          {
            old_metric_record: old_metric_record,
            new_metric_record: new_metric_record
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to update metric record. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    } finally {
      setGridLoading(false);
    }
  };

  useEffect(() => {
    try {
      setRowModesModel({});
      setMetricRecordRows([]);
      fetchRows();
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: QualitativeMetricRecordGrid failed on an unknown error while initialising.',
          null,
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to fetch metric records. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
  }, [capture_parameters.metric_group]);

  const columns: GridColDef[] = [
    {
      field: 'metric',
      headerName: 'Metric',
      valueGetter(params: GridValueGetterParams) {
        return params.row.metric.name;
      },
      renderCell: (params: GridRenderCellParams) => (
        <Box
          sx={{
            py: 2,
            maxHeight: '100px',
            display: 'flex',
            width: '100%',
            alignItems: 'center',
            textAlign: 'left',
            gap: 2.5
          }}
        >
          <Typography
            sx={{
              fontSize: '0.9rem',
              whiteSpace: 'normal',
              wordWrap: 'break-word',
              maxHeight: '90px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: 'block'
            }}
          >
            {params.row.metric.name}
          </Typography>
          <Box sx={{ ml: 'auto' }}>
            <Tooltip
              title={<Typography fontSize="0.9rem">{params.row.metric.description}</Typography>}
              arrow
            >
              <InfoOutlinedIcon color="primary" />
            </Tooltip>
          </Box>
        </Box>
      ),
      getApplyQuickFilterFn: (value: string) => {
        return (params) => {
          return params.value.name.includes(value);
        };
      },
      flex: 1.2
    },
    {
      field: 'status',
      headerName: 'Compliant?',
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: GridRenderCellParams) => {
        if (!params.row.saved) return <></>;
        if (params.value)
          return (
            <ResponseYesIcon
              height={25}
              fill="none"
              stroke={theme.palette.success.main}
              strokeWidth={5}
            />
          );
        return (
          <ResponseNoIcon
            height={25}
            fill="none"
            stroke={theme.palette.error.main}
            strokeWidth={5}
          />
        );
      },
      type: 'boolean',
      flex: 0.5,
      editable: true
    },
    {
      field: 'value',
      headerName: 'Details',
      headerAlign: 'left',
      align: 'left',
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ py: 2 }}>
          <Typography
            sx={{
              fontSize: '0.9rem',
              whiteSpace: 'normal',
              wordWrap: 'break-word',
              maxHeight: '90px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              textAlign: 'left'
            }}
          >
            {params.value}
          </Typography>
        </Box>
      ),
      renderEditCell: (params) => <CustomValueEditComponent {...params} />,
      flex: 1.25,
      editable: true
    }
  ];
  if (!view_only) {
    columns.push({
      field: 'actions',
      type: 'actions',
      headerName: '',
      align: 'right',
      flex: 0.75,
      getActions: ({ row }) => {
        const edit_row: boolean = rowModesModel[row.id]?.mode === GridRowModes.Edit;
        if (edit_row) {
          return [
            <Tooltip title="Save Changes" key={1}>
              <GridActionsCellItem
                icon={<CheckIcon sx={{ fontSize: '1.5rem' }} />}
                label="Confirm"
                sx={{
                  color: '#053d5d'
                }}
                onClick={() => handleConfirmClick(row.id)}
              />
            </Tooltip>,
            <Tooltip title="Discard Changes" key={2}>
              <GridActionsCellItem
                icon={<CancelIcon />}
                label="Cancel"
                className="textPrimary"
                color="inherit"
                onClick={() => {
                  handleCancelClick(row.id);
                }}
              />
            </Tooltip>
          ];
        }
        return [
          <>
            {row.docs && (
              <Tooltip title="Manage Supporting Documents" key={1}>
                <GridActionsCellItem
                  icon={<FolderIcon height={25} fill={theme.palette.primary.main} />}
                  label="Uploaded Documents"
                  onClick={() => handleOpenManageDocsModal(row)}
                  sx={{
                    color: '#053d5d'
                  }}
                />
              </Tooltip>
            )}
            {row.saved && (
              <Tooltip title="Upload Supporting Document" key={2}>
                <GridActionsCellItem
                  icon={<FileIcon height={25} fill={theme.palette.primary.main} />}
                  label="Supporting Documents"
                  sx={{
                    color: '#053d5d'
                  }}
                  onClick={() => handleOpenUploadDocsModal(row)}
                />
              </Tooltip>
            )}
            <Tooltip title="Edit Record" key={3}>
              <GridActionsCellItem
                icon={<EditIcon width={25} fill={theme.palette.primary.main} />}
                label="Edit"
                sx={{
                  color: 'primary.main'
                }}
                onClick={() => handleEditClick(row.id)}
              />
            </Tooltip>
            <Tooltip title="Copy from Reporting Period Group" key={4}>
              <GridActionsCellItem
                icon={
                  <CopyIcon
                    height={25}
                    fill={theme.palette.primary.main}
                    stroke={theme.palette.primary.main}
                  />
                }
                label="Copy"
                sx={{
                  color: 'primary.main'
                }}
                onClick={() => handleOpenCopyMetricRecordModal(row)}
              />
            </Tooltip>
            {row.saved && (
              <Tooltip title="Clear Response" key={5}>
                <GridActionsCellItem
                  icon={
                    <DeleteIcon
                      width={25}
                      fill={theme.palette.primary.main}
                      stroke={theme.palette.primary.main}
                    />
                  }
                  label="Cancel"
                  onClick={() => handleClearClick(row)}
                />
              </Tooltip>
            )}
          </>
        ];
      }
    });
  }

  return (
    <>
      <DeleteConfirmModal
        open={showClearConfirmModal}
        allow_delete
        allow_archive
        delete_label="the response"
        handleCloseDeleteModal={handleCloseClearModal}
        handleDelete={handleClear}
      />
      <CopyQualitativeMetricRecordModal
        open={showCopyMetricRecordModal}
        entities_map={entities_map}
        metric_record={actionRow}
        capture_parameters={capture_parameters}
        handleUpdateRow={async (
          old_metric_record: QualitativeMetricRecordGridRow,
          new_metric_record: QualitativeMetricRecordGridRow
        ) => await updateRow(old_metric_record, new_metric_record)}
        handleCloseCopyModal={handleCloseCopyMetricRecordModal}
        handleAddSupportingDoc={handleAddSupportingDoc}
        handleRemoveSupportingDoc={handleRemoveSupportingDoc}
      />
      <UploadSupportingDocsModal
        open={showUploadDocsModal}
        handleCloseDocsModal={handleCloseUploadDocsModal}
        metric_record={actionRow}
        metric_record_title={actionRow?.metric.name ?? 'Metric Record'}
        handleAddSupportingDoc={handleAddSupportingDoc}
      />
      <ManageSupportingDocsModal
        open={showManageDocsModal}
        handleCloseDocsModal={handleCloseManageDocsModal}
        handleRemoveSupportingDoc={handleRemoveSupportingDoc}
        view_only={view_only}
        metric_record={actionRow}
        metric_record_title={actionRow?.metric.name ?? 'Metric Record'}
        supporting_docs={supportingDocs.filter((supporting_doc: File) => {
          return supporting_doc.metric_record && supporting_doc.metric_record.id === actionRow?.id;
        })}
      />
      <DataGrid
        autoHeight
        rowHeight={100}
        loading={gridLoading}
        rows={metricRecordRows}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10, page: 0 }
          }
        }}
        pageSizeOptions={[10, 25, 50, 100]}
        columns={columns}
        editMode="row"
        slots={{
          toolbar: QualitativeMetricRecordGridToolbar,
          noRowsOverlay: () => <DataGridNoRows missing_label={metric_record_label} />
        }}
        slotProps={{
          toolbar: {
            metric_record_rows: metricRecordRows,
            capture_parameters: capture_parameters
          }
        }}
        rowModesModel={rowModesModel}
        disableRowSelectionOnClick
        processRowUpdate={(updatedRow, originalRow) => {
          try {
            return updateRow(originalRow, updatedRow);
          } catch (err: unknown) {
            const tracking_id: string = generateUuidV4();
            log(
              'error',
              new MetadataError(
                err instanceof Error
                  ? err.message
                  : 'Error: QualitativeMetricRecordGrid failed on an unknown error while calling updating row.',
                {
                  originalRow: originalRow,
                  updatedRow: updatedRow
                },
                tracking_id
              )
            );
            setFeedbackData({
              message: `Unable to process row update. Tracking ID: ${tracking_id}`,
              state: true,
              type: 'error'
            });
          }
        }}
        onProcessRowUpdateError={(err: unknown) => {
          const tracking_id: string = generateUuidV4();
          log(
            'error',
            new MetadataError(
              err instanceof Error
                ? err.message
                : 'Error: QualitativeMetricRecordGrid: DataGrid failed on an unknown error.',
              null,
              tracking_id
            )
          );
          setFeedbackData({
            message: `Unable to update row. Tracking ID: ${tracking_id}`,
            state: true,
            type: 'error'
          });
        }}
        sx={{ minWidth: 0 }}
      />
    </>
  );
};

export default QualitativeMetricRecordGrid;
